"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.showDiff = exports.getDiff = exports.getDiffs = exports.serializeDiffs = exports.parseDiff = exports.splitFilesInDiff = void 0;
const child_process_1 = require("child_process");
const Red = '\x1b[31m';
const Green = '\x1b[32m';
const Cyan = '\x1b[36m';
const Bold = '\x1b[1m';
const Underline = '\x1b[4m';
const Overline = '\x1b[53m'; // \u203E
const Reset = '\x1b[0m';
function openInLess(content) {
    const less = (0, child_process_1.spawn)('less', ['-R'], { stdio: ['pipe', process.stdout, process.stderr] });
    less.stdin.write(content);
    less.stdin.end();
}
// Set of animal emojis that will be mapped to files for easier identification
const ANIMALS = [
    '🐶',
    '🐱',
    '🐭',
    '🐹',
    '🐰',
    '🦊',
    '🐻',
    '🐼',
    '🐨',
    '🐯',
    '🦁',
    '🐮',
    '🐷',
    '🐸',
    '🐵',
    '🐔',
    '🐧',
    '🐦',
    '🦆',
    '🦉',
];
function splitFilesInDiff(input) {
    const lines = input.split('\n');
    const diffs = [];
    let currentDiff = '';
    for (const line of lines) {
        if (line.startsWith('diff --git')) {
            if (currentDiff) {
                diffs.push(currentDiff);
            }
            currentDiff = line;
        }
        else {
            currentDiff += '\n' + line;
        }
    }
    if (currentDiff) {
        diffs.push(currentDiff);
    }
    return diffs.filter((d) => d.trim() !== '');
}
exports.splitFilesInDiff = splitFilesInDiff;
function getLineNumber(line) {
    const lineNumberMatch = line.match(/@@ -(\d+),\d+ .* @@/);
    return lineNumberMatch ? parseInt(lineNumberMatch[1]) : null;
}
function parseDiff(diffOutput) {
    if (diffOutput.trim() === '') {
        return [];
    }
    const fileParts = splitFilesInDiff(diffOutput);
    const diffs = [];
    fileParts.forEach((part) => {
        var _a;
        const lines = part.split('\n');
        const filePath = ((_a = lines[0]
            .split(' b/')
            .pop()) === null || _a === void 0 ? void 0 : _a.trim().replace(/^"(.+)"$/, '$1')) || '';
        let currentSectionLineNumber = null;
        let currentSectionLines = [];
        const addDiff = () => {
            if (currentSectionLineNumber !== null && currentSectionLines.length > 0) {
                diffs.push({
                    path: filePath,
                    lineNumber: currentSectionLineNumber,
                    contents: currentSectionLines.join('\n'),
                });
            }
        };
        for (const line of lines) {
            const lineNumber = getLineNumber(line);
            if (lineNumber !== null) {
                addDiff();
                currentSectionLines = [];
                currentSectionLineNumber = lineNumber;
            }
            else {
                currentSectionLines.push(line);
            }
        }
        addDiff();
    });
    return diffs;
}
exports.parseDiff = parseDiff;
function getHeader(diff, options, index, includeEmoji) {
    return `${includeEmoji ? ANIMALS[index] + ' ' : ''}${diff.path}:${diff.lineNumber}`;
}
function coloredDiff(diff, options) {
    return diff.contents
        .split('\n')
        .map((line) => {
        const color = line.startsWith('+') ? Green : line.startsWith('-') ? Red : '';
        const text = !options.includePlusMinus ? line.slice(1) : line;
        return `${options.includeColors ? color : ''}${text}${options.includeColors ? Reset : ''}`;
    })
        .join('\n');
}
function coloredHeader(header, options) {
    return options.includeColors ? `${Bold}${Underline}${Cyan}${header}${Reset}` : header;
}
function coloredFooter(footer, options) {
    return options.includeColors ? `${Bold}${Overline}${Cyan}${footer}${Reset}` : footer;
}
function serializeDiffs(diffs, options) {
    if (diffs.length === 0) {
        return '';
    }
    let fileIndex = 0;
    let lastFile = diffs[0].path;
    return diffs
        .map((diff) => {
        if (diff.path !== lastFile) {
            lastFile = diff.path;
            fileIndex++;
        }
        const header = getHeader(diff, options, fileIndex, options.includeEmoji);
        const footer = options.includeFooter ? ' '.repeat(header.length) : '';
        return `${coloredHeader(header, options)}\n${coloredDiff(diff, options)}\n${coloredFooter(footer, options)}`;
    })
        .join('\n\n');
}
exports.serializeDiffs = serializeDiffs;
function getUntrackedFilesAsDiff() {
    return (0, child_process_1.execSync)('git ls-files --others --exclude-standard -z | xargs -0 -n 1 git --no-pager diff --no-index /dev/null || true', {
        encoding: 'utf8',
    });
}
function getDiffString(cached) {
    const command = cached ? 'git diff --cached' : 'git diff';
    return (0, child_process_1.execSync)(command, { encoding: 'utf8' });
}
/**
 * Get the current git diff as an array of diffs.
 *
 * @param includeUntracked [boolean] - Include untracked files in the diff output.
 * @param cached [boolean] - Show staged changes.
 * @returns [String] git diff string
 */
function getDiffs(includeUntracked, cached) {
    let diffOutput = getDiffString(cached);
    if (includeUntracked && !cached) {
        const untrackedFiles = getUntrackedFilesAsDiff();
        diffOutput += '\n\n';
        diffOutput += untrackedFiles;
    }
    return parseDiff(diffOutput);
}
exports.getDiffs = getDiffs;
/**
 * Get the current git diff as a string.
 *
 * @param options [DiffOptions] - Options for the diff output.
 * @returns [String] git diff string
 */
function getDiff(options) {
    const diffs = getDiffs(options.includeUntracked, options.cached);
    return serializeDiffs(diffs, options);
}
exports.getDiff = getDiff;
/**
 * Open the current git diff in less.
 *
 * @param options [DiffOptions] - Options for the diff output.
 */
function showDiff(options, useLess = true) {
    const diffs = getDiffs(options.includeUntracked, options.cached);
    const serialized = serializeDiffs(diffs, options);
    if (useLess) {
        openInLess(serialized);
    }
    else {
        console.log(serialized);
    }
}
exports.showDiff = showDiff;
